import { useEffect, useCallback } from 'react';

/**
 * Maps theme_config keys (from DB) to CSS custom property names.
 * DB stores: { nav_from: '#1d2f6f', primary: '#1f4e79', ... }
 * CSS uses:  --theme-nav-from, --theme-primary, ...
 */
const KEY_TO_VAR = {
  nav_from:          '--theme-nav-from',
  nav_to:            '--theme-nav-to',
  nav_active:        '--theme-nav-active',
  primary:           '--theme-primary',
  primary_dark:      '--theme-primary-dark',
  accent:            '--theme-accent',
  header_bg:         '--theme-header-bg',
  text:              '--theme-text',
  text_secondary:    '--theme-text-secondary',
  text_muted:        '--theme-text-muted',
  border:            '--theme-border',
  border_light:      '--theme-border-light',
  bg_light:          '--theme-bg-light',
  success:           '--theme-success',
  danger:            '--theme-danger',
  warning:           '--theme-warning',
  tab_active_border: '--theme-tab-active-border',
  tab_active_text:   '--theme-tab-active-text',
  font_family:       '--theme-font-family',
};

/** Apply a theme_config object to :root CSS variables */
export function applyTheme(themeConfig) {
  const root = document.documentElement;
  if (!themeConfig || typeof themeConfig !== 'object') return;
  for (const [key, cssVar] of Object.entries(KEY_TO_VAR)) {
    const val = themeConfig[key];
    if (val) {
      root.style.setProperty(cssVar, val);
    } else {
      // Remove override so :root default takes effect
      root.style.removeProperty(cssVar);
    }
  }
}

/** Reset all theme CSS variables to defaults (remove inline overrides) */
export function resetTheme() {
  const root = document.documentElement;
  for (const cssVar of Object.values(KEY_TO_VAR)) {
    root.style.removeProperty(cssVar);
  }
}

/**
 * Hook: auto-applies theme from selectedProperty.theme_config whenever property changes.
 * Call once in App.js: useTheme(selectedProperty)
 */
export default function useTheme(selectedProperty) {
  const apply = useCallback(() => {
    if (!selectedProperty) {
      resetTheme();
      return;
    }
    const cfg = selectedProperty.theme_config;
    if (cfg && typeof cfg === 'object' && Object.keys(cfg).length > 0) {
      applyTheme(cfg);
    } else {
      resetTheme();
    }
  }, [selectedProperty]);

  useEffect(() => { apply(); }, [apply]);
}
